import React, { FC } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useModal } from 'store/modal';
import UpdateEntity from 'components/crud/UpdateEntity';
import { ISelectOptionProps } from 'components/form/types';
import ClientForm from '../../components/ClientForm';
import { ICompany } from '../../types';
import { IInputs } from '../../components/types';

interface IProps {
  client: ICompany;
  typeOptions: ISelectOptionProps[];
  langOptions: ISelectOptionProps[];
}

const EditForm: FC<IProps> = ({ client, typeOptions, langOptions }: IProps) => {
  const { id } = useParams<{ id: string }>();
  const { showModal } = useModal();

  const type = typeOptions.find(option => client!.type === option.value);
  const defaultLang = langOptions.find(option => client!.default_lang === option.value);

  const methods = useForm<IInputs>({
    defaultValues: {
      ...client,
      ...client.client_data,
      ...client.client_features,
      absence_webhook_url: client.client_data?.absence_webhook_url ?? '',
      type,
      default_lang: defaultLang,
      templates: client.client_features.templates,
      is_multilingual: client.client_features.is_multilingual,
      leaves: client.client_features.leaves,
      leaves_half_day: client.client_features.leaves_half_day,
      send_leave_confirm_notification_on_approval: client.client_features.send_leave_confirm_notification_on_approval,
      form_76: client.client_features.form_76,
      is_demo: client.client_features.is_demo,
      permissions_scope: client.client_features.permissions_scope,
      filtered_by_role_permission: client.client_features.filtered_by_role_permission,
      hide_leave_templates_from_form: client.client_features.hide_leave_templates_from_form,
      show_colleagues_leaves: client.client_features.show_colleagues_leaves,
      colleagues_common_group: client.client_features.colleagues_common_group,
      leave_review: client.client_features.leave_review,
      exit_interview: client.client_features.exit_interview,
    },
  });

  return (
    <UpdateEntity
      id={id}
      entity="clients"
      methods={methods}
      onSuccess={() => {
        showModal({
          type: 'success',
          content: (
            <>
              Редактирахте клиент&nbsp;
              <span className="font-weight-bold">{id}</span>.
            </>
          ),
        });
      }}
      queryKey={['client', { id }]}
      onSuccessPushToLink="/clients"
    >
      {(update: any) => {
        return (
          <ClientForm
            methods={methods}
            handleSubmit={data => {
              const formatData = {
                ...data,
                type: data.type.value,
                default_lang: data.default_lang.value,
                absence_webhook_url: data.absence_webhook_url?.trim() || null,
                // TODO - check why pass representatives fields separately, possible to skip
                second_representative_names: data.second_representative_names ?? null,
                second_representative_egn: data.second_representative_egn ?? null,
                second_representative_position: data.second_representative_position ?? null,
                third_representative_names: data.third_representative_names ?? null,
                third_representative_egn: data.third_representative_egn ?? null,
                third_representative_position: data.third_representative_position ?? null,
              };

              // Add leave_review and exit_interview to client_features and as key of the data
              // eslint-disable-next-line @typescript-eslint/naming-convention
              const { client_features, leave_review, exit_interview, ...rest } = formatData;
              const updatedFeatures = { ...client_features, leave_review, exit_interview };
              const formatDataForUpdate = { ...rest, leave_review, exit_interview, client_features: updatedFeatures };

              return update(formatDataForUpdate);
            }}
            btnLabel="Запази"
            typeOptions={typeOptions}
            langOptions={langOptions}
            isEditMode
          />
        );
      }}
    </UpdateEntity>
  );
};

export default EditForm;
